import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Lock,
  LogOut,
  Plus,
  Trash2,
  Upload,
  Copy,
  Check,
  Image as ImageIcon,
  Loader2,
  ArrowLeft,
  Heart,
  X,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Inbox,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// sessionStorage clears when the tab/window closes — narrower XSS exposure
// than localStorage for a single-user admin dashboard.
const STORAGE_KEY = "mv_admin_key";

const slugify = (s) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const Admin = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", client_name: "", slug: "", code: "" });
  const [createErr, setCreateErr] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [copiedId, setCopiedId] = useState("");

  const [tab, setTab] = useState("galleries"); // 'galleries' | 'bookings'
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(null); // gallery object or null

  const authed = Boolean(adminKey);

  const refresh = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/galleries`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setGalleries(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setAdminKey("");
        setGalleries([]);
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  const refreshBookings = useCallback(async () => {
    if (!adminKey) return;
    setBookingsLoading(true);
    try {
      const r = await axios.get(`${API}/bookings`, {
        headers: { "X-Admin-Key": adminKey },
      });
      setBookings(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        setAdminKey("");
        setBookings([]);
      }
    } finally {
      setBookingsLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    if (!authed) return;
    if (tab === "galleries") refresh();
    if (tab === "bookings") refreshBookings();
  }, [authed, tab, refresh, refreshBookings]);

  const signIn = async (e) => {
    e.preventDefault();
    setLoginErr("");
    setLoginBusy(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      const key = r.data.admin_key;
      sessionStorage.setItem(STORAGE_KEY, key);
      setAdminKey(key);
      setPassword("");
    } catch (err) {
      setLoginErr(err?.response?.data?.detail || "Invalid password");
    } finally {
      setLoginBusy(false);
    }
  };

  const signOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setAdminKey("");
    setGalleries([]);
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setCreateErr("");
    setCreateBusy(true);
    try {
      const slug = slugify(form.slug || form.title);
      await axios.post(
        `${API}/admin/galleries`,
        { ...form, slug },
        { headers: { "X-Admin-Key": adminKey } }
      );
      setForm({ title: "", client_name: "", slug: "", code: "" });
      setShowCreate(false);
      await refresh();
    } catch (err) {
      setCreateErr(err?.response?.data?.detail || "Could not create gallery");
    } finally {
      setCreateBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this gallery and all photos? This cannot be undone.")) return;
    await axios.delete(`${API}/admin/galleries/${id}`, {
      headers: { "X-Admin-Key": adminKey },
    });
    refresh();
  };

  const copyShareLink = (g) => {
    const link = `${window.location.origin}/peek/${g.slug}`;
    navigator.clipboard.writeText(`${link}\nCode: ${g.code}`);
    setCopiedId(g.id);
    setTimeout(() => setCopiedId(""), 1500);
  };

  // ---------- Login screen ----------
  if (!authed) {
    return (
      <main
        data-testid="admin-login"
        className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-6"
      >
        <form
          onSubmit={signIn}
          className="w-full max-w-md border border-[#E6E2DA] bg-white p-10"
          data-testid="admin-login-form"
        >
          <button
            type="button"
            onClick={() => navigate("/")}
            className="overline link-underline mb-8 inline-flex items-center gap-2 text-[#5C5C5C]"
          >
            <ArrowLeft size={14} strokeWidth={1.4} /> Back to site
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Lock size={18} strokeWidth={1.2} className="text-[#C48B71]" />
            <p className="overline">Admin Access</p>
          </div>
          <h1 className="serif text-4xl font-light text-[#2A2A2A] leading-tight">
            ManeVzion <span className="italic text-[#C48B71]">studio</span>.
          </h1>
          <p className="text-[#5C5C5C] text-sm mt-2 font-light">
            Enter your password to manage client galleries.
          </p>

          <label className="overline block mt-8 mb-1">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="input-editorial"
            autoFocus
            required
          />
          {loginErr && (
            <p data-testid="admin-login-error" className="overline text-red-700 mt-3">
              {loginErr}
            </p>
          )}
          <button
            type="submit"
            disabled={loginBusy}
            data-testid="admin-login-submit"
            className="btn-primary mt-8 w-full justify-center disabled:opacity-60"
          >
            {loginBusy && <Loader2 size={16} className="animate-spin" />}
            Sign in
          </button>
        </form>
      </main>
    );
  }

  // ---------- Dashboard ----------
  return (
    <main
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#F9F8F6] px-6 md:px-10 lg:px-16 py-12"
    >
      <div className="mx-auto max-w-[1300px]">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="overline">Studio · Admin</p>
            <h1 className="serif text-4xl md:text-5xl font-light text-[#2A2A2A] mt-2">
              {tab === "bookings" ? "Recent Bookings" : "Client Galleries"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="overline link-underline text-[#5C5C5C]"
            >
              View site
            </button>
            <button
              onClick={signOut}
              data-testid="admin-signout"
              className="btn-outline"
            >
              <LogOut size={14} strokeWidth={1.4} /> Sign out
            </button>
          </div>
        </div>

        <nav
          data-testid="admin-tabs"
          className="border-b border-[#E6E2DA] flex items-center gap-8 mb-8"
        >
          <button
            onClick={() => setTab("galleries")}
            data-testid="admin-tab-galleries"
            className={`pb-4 -mb-px overline transition-colors ${
              tab === "galleries"
                ? "text-[#2A2A2A] border-b border-[#C48B71]"
                : "text-[#5C5C5C] hover:text-[#2A2A2A]"
            }`}
          >
            <ImageIcon size={12} strokeWidth={1.6} className="inline -mt-0.5 mr-2" />
            Galleries
          </button>
          <button
            onClick={() => setTab("bookings")}
            data-testid="admin-tab-bookings"
            className={`pb-4 -mb-px overline transition-colors ${
              tab === "bookings"
                ? "text-[#2A2A2A] border-b border-[#C48B71]"
                : "text-[#5C5C5C] hover:text-[#2A2A2A]"
            }`}
          >
            <Inbox size={12} strokeWidth={1.6} className="inline -mt-0.5 mr-2" />
            Bookings
          </button>
        </nav>

        {tab === "galleries" && (
          <GalleriesPanel
            galleries={galleries}
            loading={loading}
            adminKey={adminKey}
            showCreate={showCreate}
            setShowCreate={setShowCreate}
            form={form}
            setForm={setForm}
            createErr={createErr}
            createBusy={createBusy}
            onCreate={onCreate}
            onDelete={onDelete}
            copiedId={copiedId}
            copyShareLink={copyShareLink}
            refresh={refresh}
            onViewFavorites={(g) => setFavoritesOpen(g)}
          />
        )}

        {tab === "bookings" && (
          <BookingsPanel
            bookings={bookings}
            loading={bookingsLoading}
            onRefresh={refreshBookings}
          />
        )}
      </div>

      {favoritesOpen && (
        <FavoritesModal
          gallery={favoritesOpen}
          adminKey={adminKey}
          onClose={() => setFavoritesOpen(null)}
        />
      )}
    </main>
  );
};

const GalleriesPanel = ({
  galleries,
  loading,
  adminKey,
  showCreate,
  setShowCreate,
  form,
  setForm,
  createErr,
  createBusy,
  onCreate,
  onDelete,
  copiedId,
  copyShareLink,
  refresh,
  onViewFavorites,
}) => {
  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <p className="text-[#5C5C5C] text-sm font-light">
          {galleries.length} {galleries.length === 1 ? "gallery" : "galleries"}
        </p>
        <button
          onClick={() => setShowCreate((s) => !s)}
          data-testid="admin-new-gallery"
          className="btn-primary"
        >
          <Plus size={14} strokeWidth={1.6} /> New gallery
        </button>
      </div>

      {showCreate && (
        <form
          onSubmit={onCreate}
          data-testid="admin-create-form"
          className="border border-[#E6E2DA] bg-white p-8 mb-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5"
        >
          <div>
            <label className="overline block mb-1">Gallery title</label>
            <input
              required
              className="input-editorial"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Maya & Jordan — Wedding"
              data-testid="admin-create-title"
            />
          </div>
          <div>
            <label className="overline block mb-1">Client name</label>
            <input
              required
              className="input-editorial"
              value={form.client_name}
              onChange={(e) => setForm({ ...form, client_name: e.target.value })}
              placeholder="Maya & Jordan"
              data-testid="admin-create-client"
            />
          </div>
          <div>
            <label className="overline block mb-1">URL slug</label>
            <input
              required
              className="input-editorial"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              onBlur={() => setForm((f) => ({ ...f, slug: slugify(f.slug || f.title) }))}
              placeholder="maya-jordan-2026"
              data-testid="admin-create-slug"
            />
            <p className="text-xs text-[#5C5C5C] mt-1 font-light">
              Will be: /peek/{slugify(form.slug || form.title) || "your-slug"}
            </p>
          </div>
          <div>
            <label className="overline block mb-1">Access code</label>
            <input
              required
              className="input-editorial"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="MAYA2026"
              data-testid="admin-create-code"
            />
          </div>
          {createErr && (
            <p
              data-testid="admin-create-error"
              className="overline text-red-700 md:col-span-2"
            >
              {createErr}
            </p>
          )}
          <div className="md:col-span-2 flex gap-3 pt-2">
            <button
              type="submit"
              disabled={createBusy}
              data-testid="admin-create-submit"
              className="btn-primary disabled:opacity-60"
            >
              {createBusy && <Loader2 size={14} className="animate-spin" />}
              Create gallery
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="btn-outline"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-20 overline text-[#5C5C5C]">Loading…</div>
      ) : galleries.length === 0 ? (
        <div
          data-testid="admin-empty"
          className="border border-dashed border-[#E6E2DA] py-24 text-center"
        >
          <ImageIcon size={28} strokeWidth={1} className="mx-auto text-[#C48B71]" />
          <p className="serif text-2xl mt-4 text-[#2A2A2A]">No galleries yet</p>
          <p className="text-[#5C5C5C] text-sm mt-1 font-light">
            Click "New gallery" to create your first private client gallery.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {galleries.map((g) => (
            <GalleryCard
              key={g.id}
              gallery={g}
              adminKey={adminKey}
              onDelete={() => onDelete(g.id)}
              onCopyLink={() => copyShareLink(g)}
              copied={copiedId === g.id}
              refresh={refresh}
              onViewFavorites={() => onViewFavorites(g)}
            />
          ))}
        </div>
      )}
    </>
  );
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
};

const BookingsPanel = ({ bookings, loading, onRefresh }) => {
  if (loading) {
    return <div className="text-center py-20 overline text-[#5C5C5C]">Loading…</div>;
  }
  if (bookings.length === 0) {
    return (
      <div
        data-testid="admin-bookings-empty"
        className="border border-dashed border-[#E6E2DA] py-24 text-center"
      >
        <Inbox size={28} strokeWidth={1} className="mx-auto text-[#C48B71]" />
        <p className="serif text-2xl mt-4 text-[#2A2A2A]">No bookings yet</p>
        <p className="text-[#5C5C5C] text-sm mt-1 font-light">
          Inquiries submitted from the booking form will appear here.
        </p>
      </div>
    );
  }
  return (
    <div data-testid="admin-bookings-list" className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-[#5C5C5C] text-sm font-light">
          {bookings.length} inquir{bookings.length === 1 ? "y" : "ies"} · newest first
        </p>
        <button onClick={onRefresh} className="btn-outline" data-testid="admin-bookings-refresh">
          Refresh
        </button>
      </div>
      {bookings.map((b) => (
        <article
          key={b.id}
          data-testid={`admin-booking-${b.id}`}
          className="border border-[#E6E2DA] bg-white p-6 md:p-8 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-6"
        >
          <div>
            <p className="overline text-[#C48B71]">{b.session_type}</p>
            <h3 className="serif text-2xl mt-1 text-[#2A2A2A] leading-tight">
              {b.name}
            </h3>
            <p className="overline text-[#5C5C5C] mt-2">
              {formatDate(b.created_at)}
            </p>
            {b.message && (
              <p className="text-[#2A2A2A] text-sm mt-4 font-light leading-relaxed bg-[#F2EFE9] p-4">
                "{b.message}"
              </p>
            )}
          </div>
          <div className="flex flex-col gap-3 text-sm md:border-l md:border-[#E6E2DA] md:pl-6">
            <a
              href={`mailto:${b.email}`}
              className="inline-flex items-center gap-2 text-[#2A2A2A] hover:text-[#C48B71] transition-colors break-all"
              data-testid={`admin-booking-email-${b.id}`}
            >
              <Mail size={14} strokeWidth={1.4} className="text-[#C48B71] shrink-0" />
              {b.email}
            </a>
            {b.phone && (
              <a
                href={`tel:${b.phone}`}
                className="inline-flex items-center gap-2 text-[#2A2A2A] hover:text-[#C48B71] transition-colors"
              >
                <Phone size={14} strokeWidth={1.4} className="text-[#C48B71] shrink-0" />
                {b.phone}
              </a>
            )}
            <p className="inline-flex items-center gap-2 text-[#2A2A2A]">
              <Calendar size={14} strokeWidth={1.4} className="text-[#C48B71] shrink-0" />
              {b.preferred_date}
            </p>
            {b.location && (
              <p className="inline-flex items-center gap-2 text-[#2A2A2A]">
                <MapPin size={14} strokeWidth={1.4} className="text-[#C48B71] shrink-0" />
                {b.location}
              </p>
            )}
          </div>
        </article>
      ))}
    </div>
  );
};

const FavoritesModal = ({ gallery, adminKey, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/admin/galleries/${gallery.id}/favorites`,
          { headers: { "X-Admin-Key": adminKey } }
        );
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Could not load favorites");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gallery.id, adminKey]);

  const photoSrc = (photoId) =>
    `${API}/peek/${gallery.slug}/photo/${photoId}?code=${encodeURIComponent(gallery.code)}`;

  return (
    <div
      data-testid="admin-favorites-modal"
      className="fixed inset-0 z-[60] bg-[#2A2A2A]/80 flex items-start md:items-center justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl bg-[#F9F8F6] border border-[#E6E2DA] my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="admin-favorites-close"
          aria-label="Close"
          className="absolute top-4 right-4 text-[#5C5C5C] hover:text-[#2A2A2A]"
        >
          <X size={22} strokeWidth={1.3} />
        </button>
        <div className="px-8 pt-10 pb-6 border-b border-[#E6E2DA]">
          <p className="overline text-[#C48B71] inline-flex items-center gap-2">
            <Heart size={12} strokeWidth={1.6} fill="#C48B71" /> Client Favorites
          </p>
          <h2 className="serif text-3xl md:text-4xl font-light text-[#2A2A2A] mt-2">
            {gallery.title}
          </h2>
          <p className="text-[#5C5C5C] text-sm mt-1 font-light">
            {gallery.client_name} · /peek/{gallery.slug}
          </p>
        </div>

        <div className="p-8">
          {loading ? (
            <p className="overline text-[#5C5C5C] text-center py-12">Loading…</p>
          ) : err ? (
            <p className="overline text-red-700 text-center py-12">{err}</p>
          ) : !data || data.favorites.length === 0 ? (
            <div className="text-center py-16">
              <Heart size={28} strokeWidth={1} className="mx-auto text-[#C48B71]" />
              <p className="serif text-2xl mt-4 text-[#2A2A2A]">No favorites yet</p>
              <p className="text-[#5C5C5C] text-sm mt-1 font-light">
                When your client hearts photos, they'll show up right here.
              </p>
            </div>
          ) : (
            <>
              <p className="overline text-[#5C5C5C] mb-5">
                {data.favorites.length} photo{data.favorites.length === 1 ? "" : "s"} hearted
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {data.favorites.map((f) => (
                  <a
                    key={f.photo_id}
                    href={photoSrc(f.photo_id) + "&download=1"}
                    data-testid={`admin-favorite-${f.photo_id}`}
                    className="img-hover relative aspect-[3/4] bg-[#E6E2DA] overflow-hidden block"
                    title={`${f.filename} · favorited ${formatDate(f.favorited_at)}`}
                  >
                    <img
                      src={photoSrc(f.photo_id)}
                      alt={f.filename}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    <span className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full bg-[#C48B71] text-white">
                      <Heart size={13} strokeWidth={1.6} fill="currentColor" />
                    </span>
                    <span className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/65 to-transparent text-white text-[10px] tracking-[0.18em] uppercase px-3 py-2 truncate">
                      {f.filename}
                    </span>
                  </a>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const GalleryCard = ({ gallery, adminKey, onDelete, onCopyLink, copied, refresh, onViewFavorites }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setUploadErr("");
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/admin/galleries/${gallery.id}/photos`, fd, {
        headers: { "X-Admin-Key": adminKey, "Content-Type": "multipart/form-data" },
      });
      await refresh();
    } catch (err) {
      setUploadErr(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <article
      data-testid={`admin-gallery-${gallery.slug}`}
      className="border border-[#E6E2DA] bg-white p-6 flex flex-col"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="overline text-[#C48B71]">/peek/{gallery.slug}</p>
          <h3 className="serif text-2xl mt-1 leading-tight text-[#2A2A2A]">
            {gallery.title}
          </h3>
          <p className="text-sm text-[#5C5C5C] font-light mt-1">{gallery.client_name}</p>
        </div>
        <button
          onClick={onDelete}
          data-testid={`admin-delete-${gallery.slug}`}
          aria-label="Delete gallery"
          className="text-[#5C5C5C] hover:text-red-700 transition-colors"
        >
          <Trash2 size={16} strokeWidth={1.3} />
        </button>
      </div>

      <div className="border-t border-[#E6E2DA] mt-5 pt-4 flex items-center justify-between text-sm">
        <span className="overline">Code · {gallery.code}</span>
        <span className="overline">{gallery.photo_count} photos</span>
      </div>

      <div className="mt-5 flex flex-col gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={onFiles}
          className="hidden"
          data-testid={`admin-upload-input-${gallery.slug}`}
        />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid={`admin-upload-${gallery.slug}`}
          className="btn-outline w-full justify-center disabled:opacity-60"
        >
          {uploading ? (
            <>
              <Loader2 size={14} className="animate-spin" /> Uploading…
            </>
          ) : (
            <>
              <Upload size={14} strokeWidth={1.4} /> Upload photos
            </>
          )}
        </button>
        <button
          onClick={onCopyLink}
          data-testid={`admin-copy-${gallery.slug}`}
          className="btn-primary w-full justify-center"
        >
          {copied ? (
            <>
              <Check size={14} /> Copied
            </>
          ) : (
            <>
              <Copy size={14} strokeWidth={1.4} /> Copy share link
            </>
          )}
        </button>
        <button
          onClick={onViewFavorites}
          data-testid={`admin-favorites-${gallery.slug}`}
          className="btn-outline w-full justify-center"
        >
          <Heart size={14} strokeWidth={1.4} /> View favorites
        </button>
        {uploadErr && (
          <p
            data-testid={`admin-upload-error-${gallery.slug}`}
            className="overline text-red-700 mt-1"
          >
            {uploadErr}
          </p>
        )}
      </div>
    </article>
  );
};

export default Admin;
